import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { useCallback, useMemo } from 'react'

/**
 * Custom hook for URL-based file navigation in agent pages (GitHub-style clean URLs)
 * Uses pathname-based URLs instead of query parameters for cleaner, more readable URLs
 * 
 * Example URLs:
 * - /agentes/abc123/configuracao/identity/writing_style/tonal_profile.md
 * - /agentes/abc123/conversas/conversa-123
 * 
 * @param agentId - The ID of the current agent
 * @param basePath - The base path for this section (e.g., 'configuracao', 'conversas')
 * @returns Navigation utilities for file selection via URL
 */
export function useAgentFileNavigation(agentId: string, basePath: string = 'configuracao') {
    const navigate = useNavigate()
    const location = useLocation()
    const params = useParams<{ handle: string }>()

    // Extract file path from URL pathname
    // Example: /agentes/khgds/configuracao/identity/writing_style/file.md
    // Returns: identity/writing_style/file.md
    const currentFileFromUrl = useMemo(() => {
        const baseRoute = `/agentes/${params.handle || agentId}/${basePath}/`
        if (location.pathname.startsWith(baseRoute)) {
            const filePath = location.pathname.slice(baseRoute.length)
            return filePath || null
        }
        return null
    }, [location.pathname, params.handle, agentId, basePath])

    /**
     * Navigate to a specific file (updates URL path)
     * Example: navigateToFile('identity/writing_style/tonal_profile.md')
     * Results in: /agentes/abc123/configuracao/identity/writing_style/tonal_profile.md
     */
    const navigateToFile = useCallback((filePath: string) => {
        const cleanPath = filePath.startsWith('/') ? filePath.slice(1) : filePath
        navigate(`/agentes/${params.handle || agentId}/${basePath}/${cleanPath}`)
    }, [navigate, params.handle, agentId, basePath])

    /**
     * Clear file selection (navigate back to base path)
     */
    const clearFileSelection = useCallback(() => {
        navigate(`/agentes/${params.handle || agentId}/${basePath}`)
    }, [navigate, params.handle, agentId, basePath])

    /**
     * Check if a specific file is currently selected via URL
     */
    const isFileSelected = useCallback((filePath: string) => {
        return currentFileFromUrl === filePath
    }, [currentFileFromUrl])

    return {
        currentFileFromUrl,
        navigateToFile,
        clearFileSelection,
        isFileSelected
    }
}
